// petconnect-v2/frontend/src/components/features/Search/VisualSearchPage.js
//
// AI-powered photo + text search page.
// Calls POST /api/search/visual with a FormData payload.
// The `visual_search_enabled` flag in the response tells the UI whether
// CLIP matching was actually applied, so we can show/hide the AI badge.

import React, { useState, useRef } from 'react';
import { Camera, Search, X, MapPin, Upload, Loader, Zap } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import LocationPicker from './LocationPicker';
import Pagination from '../../common/Pagination';
import { API_URL } from '../../../utils/constants';

// ── helpers ──────────────────────────────────────────────────────────────────

const normalisePost = (post) => {
  if (Array.isArray(post.media_urls)) return post;
  if (post.media_url) return { ...post, media_urls: [post.media_url] };
  return { ...post, media_urls: [] };
};

const SafeImage = ({ src, alt, className }) => {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-4xl ${className}`}>
        🐾
      </div>
    );
  }
  return (
    <img src={src} alt={alt} className={className} onError={() => setErrored(true)} />
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const VisualSearchPage = () => {
  const { isAnonymous } = useAuth();

  // form
  const [queryImage, setQueryImage]   = useState(null);
  const [previewUrl, setPreviewUrl]   = useState(null);
  const [textQuery, setTextQuery]     = useState('');
  const [animalType, setAnimalType]   = useState('');
  const [category, setCategory]       = useState('all');
  const [location, setLocation]       = useState(null);
  const [radiusKm, setRadiusKm]       = useState(100);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // results
  const [results, setResults]             = useState([]);
  const [total, setTotal]                 = useState(0);
  const [currentPage, setCurrentPage]     = useState(1);
  const [searching, setSearching]         = useState(false);
  const [hasSearched, setHasSearched]     = useState(false);
  const [error, setError]                 = useState('');
  // API tells us whether CLIP was actually used
  const [aiEnabled, setAiEnabled]         = useState(false);
  const [imageProvided, setImageProvided] = useState(false);

  const fileInputRef = useRef(null);
  const PAGE_SIZE = 20;

  // ── image picker ──────────────────────────────────────────────────────────

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError('Image must be under 8 MB');
      return;
    }
    setQueryImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const clearImage = () => {
    setQueryImage(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── search ────────────────────────────────────────────────────────────────

  const handleSearch = async (page = 1) => {
    if (!queryImage && !textQuery.trim() && !animalType) {
      setError('Please upload a photo, enter a description, or select an animal type.');
      return;
    }

    setSearching(true);
    setHasSearched(true);
    setError('');
    setCurrentPage(page);

    try {
      const fd = new FormData();
      if (queryImage)          fd.append('query_image', queryImage);
      if (textQuery.trim())    fd.append('text_query', textQuery.trim());
      if (animalType)          fd.append('animal_type', animalType);
      if (category !== 'all')  fd.append('category', category);
      if (location) {
        fd.append('latitude',  location.latitude.toString());
        fd.append('longitude', location.longitude.toString());
      }
      fd.append('radius_km', radiusKm.toString());
      fd.append('page',      page.toString());
      fd.append('page_size', PAGE_SIZE.toString());

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/search/visual`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Search failed' }));
        throw new Error(err.detail || 'Search failed');
      }

      const data = await res.json();
      setResults((data.posts || []).map(normalisePost));
      setTotal(data.total || 0);
      setAiEnabled(data.visual_search_enabled === true);
      setImageProvided(data.image_provided === true);
    } catch (err) {
      setError(err.message);
      setResults([]);
      setTotal(0);
    } finally {
      setSearching(false);
    }
  };

  const handlePageChange = (page) => {
    handleSearch(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── badge helpers ─────────────────────────────────────────────────────────

  const SearchModeBadge = () => {
    if (!hasSearched) return null;
    if (aiEnabled && imageProvided) {
      return (
        <span className="inline-flex items-center gap-1 text-xs bg-violet-100 text-violet-800 font-semibold px-2.5 py-1 rounded-full">
          <Zap size={11} />
          AI photo match active
        </span>
      );
    }
    if (aiEnabled && !imageProvided) {
      return (
        <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-800 font-semibold px-2.5 py-1 rounded-full">
          AI text match active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 font-semibold px-2.5 py-1 rounded-full">
        Text + location ranking
      </span>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto pb-12">

      {/* Hero */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-2xl shadow-xl p-8 mb-8">
        <h1 className="text-3xl md:text-4xl font-black mb-2 flex items-center gap-3">
          <Camera size={36} />
          AI Photo Search
        </h1>
        <p className="text-violet-200 text-sm md:text-base">
          Upload a photo of a pet you found — our AI compares it against missing-pet posts
          using visual similarity.
          {!aiEnabled && hasSearched && (
            <span className="block mt-1 text-violet-300 text-xs">
              (AI photo matching is currently disabled on this server — results are ranked by text and location.)
            </span>
          )}
        </p>
      </div>

      {/* Search panel */}
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 space-y-6">

        {/* Photo upload */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Pet Photo
            <span className="ml-1 text-gray-400 font-normal">
              {aiEnabled || !hasSearched ? '(recommended for AI matching)' : '(upload will be ignored — AI disabled)'}
            </span>
          </label>

          {previewUrl ? (
            <div className="relative inline-block">
              <img
                src={previewUrl}
                alt="Query pet"
                className="w-48 h-48 object-contain rounded-xl border-2 border-violet-300 bg-gray-50"
              />
              <button
                onClick={clearImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition shadow"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label
              htmlFor="visual-upload"
              className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-violet-300 rounded-xl cursor-pointer hover:border-violet-500 hover:bg-violet-50 transition"
            >
              <Upload size={32} className="text-violet-400 mb-2" />
              <span className="text-violet-700 font-semibold">Click to upload a photo</span>
              <span className="text-xs text-gray-400 mt-1">JPEG, PNG — max 8 MB</span>
            </label>
          )}
          <input
            ref={fileInputRef}
            type="file"
            id="visual-upload"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
        </div>

        {/* Text description */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Description <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
            placeholder="e.g. Golden retriever with white paws, friendly, wearing a blue collar…"
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-400 outline-none text-sm"
          />
        </div>

        {/* Filters row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* Animal type */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide">
              Animal type
            </label>
            <select
              value={animalType}
              onChange={(e) => setAnimalType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-400 outline-none"
            >
              <option value="">All animals</option>
              <option value="dog">🐕 Dog</option>
              <option value="cat">🐈 Cat</option>
              <option value="bird">🦜 Bird</option>
              <option value="rabbit">🐰 Rabbit</option>
              <option value="other">🐾 Other</option>
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide">
              Post type
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { v: 'all',     l: 'All' },
                { v: 'missing', l: '🔴 Missing' },
                { v: 'found',   l: '🟢 Found' },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setCategory(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition ${
                    category === v
                      ? 'bg-violet-500 text-white border-violet-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-violet-400'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide">
              Location
            </label>
            <button
              type="button"
              onClick={() => setShowLocationPicker(true)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-medium transition truncate ${
                location
                  ? 'bg-green-50 border-green-400 text-green-800'
                  : 'bg-white border-gray-300 text-gray-500 hover:border-violet-400'
              }`}
            >
              <MapPin size={14} className="flex-shrink-0" />
              {location ? location.name : 'Pick on map (optional)'}
              {location && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLocation(null); }}
                  className="ml-auto text-gray-400 hover:text-red-500"
                >
                  <X size={12} />
                </button>
              )}
            </button>
            {location && (
              <div className="mt-1">
                <input
                  type="range"
                  min="10" max="300" step="10"
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-violet-200 rounded-lg appearance-none cursor-pointer"
                />
                <p className="text-xs text-violet-600 mt-0.5">Radius: {radiusKm} km</p>
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={() => handleSearch(1)}
          disabled={searching}
          className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-black rounded-xl flex items-center justify-center gap-2 transition shadow-md disabled:opacity-50"
        >
          {searching ? (
            <>
              <Loader size={18} className="animate-spin" />
              Searching…
            </>
          ) : (
            <>
              <Search size={18} />
              Find Matching Pets
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {hasSearched && !searching && (
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-gray-800">
              {total > 0
                ? `${total} matching pet${total !== 1 ? 's' : ''} found`
                : 'No matches found'}
            </h2>
            <SearchModeBadge />
          </div>

          {total === 0 ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4">🐾</div>
              <p className="text-gray-500 font-semibold">No pets matched your search.</p>
              <p className="text-gray-400 text-sm mt-2">
                Try a different photo, broaden the radius, or remove the category filter.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {results.map((post) => (
                  <ResultCard key={post.id} post={post} />
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Pre-search placeholder */}
      {!hasSearched && (
        <div className="bg-white rounded-2xl shadow-xl p-16 text-center">
          <Camera size={56} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg font-semibold">
            Upload a pet photo or enter a description to search
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Results are ranked by visual similarity, description, and location.
          </p>
        </div>
      )}

      {/* Location picker modal */}
      {showLocationPicker && (
        <LocationPicker
          onLocationSelected={(loc) => { setLocation(loc); setShowLocationPicker(false); }}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </div>
  );
};

// ── Result card ───────────────────────────────────────────────────────────────

const ResultCard = ({ post }) => {
  const primaryUrl = post.media_urls?.[0] ?? null;

  const categoryColor =
    post.category === 'found'   ? 'bg-green-500' :
    post.category === 'missing' ? 'bg-red-500'   : 'bg-blue-500';

  const categoryLabel =
    post.category === 'found'   ? '🟢 FOUND'   :
    post.category === 'missing' ? '🔴 MISSING' :
    post.category.toUpperCase();

  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 hover:shadow-lg transition overflow-hidden flex flex-col">

      {/* Image */}
      <div className="relative w-full" style={{ paddingTop: '66.67%' }}>
        {primaryUrl ? (
          <SafeImage
            src={primaryUrl}
            alt={post.pet_name || 'Pet'}
            className="absolute inset-0 w-full h-full object-contain bg-gray-50"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-5xl">
            🐾
          </div>
        )}
        <span
          className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold text-white ${categoryColor}`}
        >
          {categoryLabel}
        </span>
        {post.status === 'resolved' && (
          <span className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            ✓ Reunited
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-3 flex flex-col flex-1">
        {post.pet_name && (
          <h3 className="font-black text-gray-900 text-base mb-0.5">
            🐾 {post.pet_name}
            {post.pet_type && (
              <span className="text-xs font-normal text-gray-500"> ({post.pet_type})</span>
            )}
          </h3>
        )}

        <p className="text-xs text-gray-500 mb-2">
          by {post.author?.username} ·{' '}
          {post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}
        </p>

        <p className="text-sm text-gray-700 line-clamp-3 flex-1 mb-2">{post.content}</p>

        {post.owner_message && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2">
            <p className="text-xs text-amber-900 line-clamp-2">💬 {post.owner_message}</p>
          </div>
        )}

        {post.location_name && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
            <MapPin size={11} />
            <span className="truncate">{post.location_name}</span>
          </div>
        )}

        {post.contact_info && (
          <p className="text-sm text-blue-600 font-semibold">📞 {post.contact_info}</p>
        )}
      </div>
    </div>
  );
};

export default VisualSearchPage;